"use client";

import { DEMO_CALENDAR_LINK } from "@/lib/constants/booking";
import "@/app/(public)/shared/tokens/colors.css";
import "@/app/(public)/shared/tokens/typography.css";
import LogoWall from "@/app/(public)/shared/components/LogoWall";
import Testimonial from "@/app/(public)/shared/components/Testimonial";
import { CTA } from "@/app/(public)/shared/components/CTA";
import { UnifiedModal, useModalManager } from "@/app/(public)/shared/components/UnifiedModal";
import { Button } from "@/app/(public)/shared/ui/Button";
import Image from "next/image";
import enterpriseGraphic from "@/app/(public)/shared/assets/graphics/enterprise.webp";
import { CheckCircle } from "lucide-react";
import FAQComponent from "@/features/landing-pages/_components/components/faq/FAQComponent";
import { LandingPageComponent } from "@/lib/database/landing-pages";

export default function Enterprise() {
  const { openModal, closeModal, isModalOpen } = useModalManager();

  // FAQ data
  const faqComponent: LandingPageComponent = {
    id: "enterprise-faq",
    type: "faq",
    version: 0,
    config: {
      items: [
        {
          question: "Do you have any TMC integrations or partnerships in place?",
          answer:
            "Yes, we do already have TMC integrations in place! We're building open APIs so TMCs can leverage our sourcing engine while keeping their client relationships intact.",
        },
        {
          question: "Does Nowadays integrate with contract management software?",
          answer:
            "Yes. We integrate with standard tools like Sertifi and DocuSign for e-signatures and redlines. We're also developing automated clause comparison, allowing users to instantly see what's changed between versions and flag exceptions against preferred language.",
        },
        {
          question: "How are you integrating human-in-the-loop workflows with your AI telephone service?",
          answer:
            "Every AI call is monitored by a human-in-the-loop system that detects ambiguity or escalation triggers (e.g., billing questions or complex contract terms). When that happens, it routes the interaction to a live team member or pauses for review before finalizing actions. This ensures speed without sacrificing accuracy or compliance.",
        },
        {
          question: "How does the AI handle phone conversations with the other party when they answer?",
          answer:
            "It interacts as normal - confirming details, requesting contact info, or clarifying RFP questions, but operates faster because it's connected directly to our system and communicates in the hotel's local time zone and language. For example, if it calls a property to confirm the correct group sales contact, it can instantly send a follow-up email from our system so the recipient can confirm receipt within seconds.",
        },
        {
          question: "Where does your source data come from, and how often is it refreshed?",
          answer:
            "We partner directly with CVBs, hotel chains, and independent venues for the most up-to-date data feeds. They let us know when there are new properties opening that don't show up on Google Maps yet. In parallel, our AI agents continuously refresh this information by running automated web and email checks, similar to what a planner would do manually but at scale. Whenever a hotel shares updated details or promotions, our system automatically parses the information and updates the database in real time.",
        },
        {
          question: "Do you source non-hotel venues as well?",
          answer:
            "Yes! Nowadays sources over 400,000+ vetted venues and vendors worldwide, including restaurants, rooftops, museums, ranches, and wineries.\n\nAfter booking hotel rooms, clients often use Nowadays to source venues, A/V, catering, and transportation in one place — saving weeks of back-and-forth.\n\nOur platform:\n• Instantly matches spaces to budget, group size, and location needs\n• Centralizes all RFPs and proposals in one dashboard for faster decisions and approvals",
        },
        {
          question: "Does your platform use rack rates, negotiated rates, or both?",
          answer:
            "Negotiated rates. For groups of 10 or more rooms, all requests are routed to hotel group sales teams – meaning there's no such thing as a true \"rack rate.\" Any platform that promises instant group booking isn't accessing the best available pricing. Our AI agents contact hotel sales managers directly and use historical data and benchmarks from thousands of past RFPs to negotiate and secure below-market group rates.",
        },
        {
          question: "How are hotel commissions managed, and how does that revenue stream affect your pricing model?",
          answer:
            "We manage commissions in-house for direct corporate clients. For 3rd-party agencies that prefer to take commission directly from hotels, we adjust our model to a flat annual subscription or technology fee — keeping incentives aligned while avoiding double-counting.",
        },
        {
          question: "What is your pricing model—per event, per attendee, or another structure?",
          answer:
            "• A-la-carte sourcing fee: a small percentage per event for ad-hoc use.\n• Annual or volume-based pricing: for repeat clients with multiple events per year.\n• Agency / Partner model: subscription or commission-share structure depending on workflow integration.",
        },
        {
          question:
            "What is the average meeting size planned through Nowadays (e.g., number of travelers, duration, order value, etc.)?",
          answer:
            "We plan all types of meetings, anything with groups larger than 10 we can handle. The average event budget: $90K–$160K and average size would be about 20-300 people.",
        },
        {
          question: "How does the product handle contract signing and potential legal review challenges?",
          answer:
            "Users can upload or link their company's preferred contract templates, and our AI flags deviations — such as indemnity clauses, attrition thresholds, or cancellation policies — before signing. Once finalized, signatures and version history are stored in a centralized audit trail for compliance and record-keeping. In addition, we have a list of standard negotiation items such as getting 1 complimentary room for every 35 rooms that we ask for every contract, but we prioritize what the client cares about the most.",
        },
        {
          question: "For production companies or extended engagements, can you source multiple weeks at once?",
          answer:
            "Yes! This is one of our strengths. Our comparison engine seamlessly handles multi-week sourcing for long-term shoots, residencies, or touring productions — allowing planners to compare extended-stay rates and F&B packages side by side for true apples-to-apples analysis. This ensures clients can identify the optimal dates and durations for their events based on both availability and budget.",
        },
        {
          question: "Have you explored using Nowadays' technology for business transient hotel program sourcing?",
          answer:
            "Not yet. We're focused on transforming the group and meetings space first – that's where we see the biggest opportunity for innovation. That said, we're open to partnering with existing transient-sourcing platforms in the future.",
        },
        {
          question: "Can you share more about your partnership with Northstar?",
          answer:
            "We power Northstar Meetings Group's venue-sourcing technology – enabling their global network of event planners and agencies to use Nowadays' AI platform for faster, more accurate RFP workflows and real-time pricing visibility. This partnership brings our automation and data intelligence to thousands of planners across Northstar's ecosystem.",
        },
      ],
    },
  };

  return (
    <main className="bg-landing-primary">
      {/* Hero Section */}
      <section className="min-h-[calc(100vh-64px)] lg:h-[calc(100vh-64px)] py-8 mt-[64px]">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-16 h-full">
          <div className="flex flex-col justify-center gap-8 md:gap-16 lg:gap-24">
            <div className="flex flex-col gap-6">
              <h1 className="text-h1 text-landing-primary">Drive strategy. Strengthen culture. Spark innovation.</h1>
              <p className="text-body-lg text-landing-secondary max-w-530">
                At Nowadays, we don't just plan events - we power your business objectives. Whether it's a global
                summit, multi-city roadshow, or quarterly strategy session, we help enterprise teams execute seamless,
                impactful events at scale.
              </p>
              <div className="flex gap-3">
                <Button variant="primary" color="primary" size="md" onClick={() => openModal("demo")}>
                  Request a demo
                </Button>
              </div>
            </div>

            {/* Static Logo Wall */}
            <LogoWall
              variant="small-static"
              type="enterprise-small"
              trustedByText="Trusted by leading enterprises like"
              className="justify-start"
            />
          </div>
          <div className="flex items-center justify-center lg:h-full mt-8 lg:mt-0">
            <div className="w-full max-w-lg lg:max-w-none">
              <Image
                src={enterpriseGraphic}
                alt="Enterprise solution illustration"
                className="w-full h-auto object-contain rounded-2xl"
                priority
              />
            </div>
          </div>
        </div>
      </section>

      {/* Strategic Partner Section - Document Style */}
      <section className="py-24">
        <div className="max-w-3xl mx-auto">
          {/* Section Header */}
          <div className="mb-16">
            <h2 className="text-h2 text-landing-primary mb-6">A strategic partner in every sense</h2>
            <p className="text-body-lg text-landing-secondary max-w-2xl">
              We act as an extension of your team - bringing structure, speed, and creativity to everything from C-level
              offsites to high-touch client summits.
            </p>
          </div>

          {/* Content */}
          <div className="space-y-12">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-primary" />
                <h3 className="text-h3 text-landing-primary">Venue sourcing & negotiation</h3>
              </div>
              <p className="text-body-lg text-landing-secondary leading-relaxed">
                Access exclusive rates and save 10–50% across a global network of 400K+ venues. Our established
                relationships and volume commitments translate to significant cost savings for your enterprise events.
                From boardrooms to ballrooms, we secure the perfect spaces at unbeatable rates.
              </p>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-primary" />
                <h3 className="text-h3 text-landing-primary">Program design & execution</h3>
              </div>
              <p className="text-body-lg text-landing-secondary leading-relaxed">
                From scheduling to A/V to branded swag—we handle the details so you don't have to. Our comprehensive
                approach ensures seamless execution of every element, allowing you to focus on your strategic objectives
                while we manage the operational complexity.
              </p>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-primary" />
                <h3 className="text-h3 text-landing-primary">Data-driven budgeting & forecasting</h3>
              </div>
              <p className="text-body-lg text-landing-secondary leading-relaxed">
                Track spend, optimize ROI, and make smarter decisions for future events. Our analytics platform provides
                detailed insights into event performance, cost allocation, and return on investment to inform your
                planning and budgeting decisions.
              </p>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-primary" />
                <h3 className="text-h3 text-landing-primary">Event intelligence</h3>
              </div>
              <p className="text-body-lg text-landing-secondary leading-relaxed">
                Get post-event insights to measure engagement, impact, and alignment with company goals. Comprehensive
                reporting helps you understand what worked, what didn't, and how to optimize future events for maximum
                impact and ROI.
              </p>
            </div>
          </div>
        </div>

        {/* Built for Scale Section */}
        <div className="max-w-3xl mx-auto mt-24">
          <div className="mb-16">
            <h2 className="text-h2 text-landing-primary mb-6">Built for scale. Designed for flexibility.</h2>
            <p className="text-body-lg text-landing-secondary max-w-2xl">
              Whether you're managing dozens of local meetups or a centralized global conference, Nowadays offers tools
              and white-glove support for every event size and type.
            </p>
          </div>

          <div className="space-y-12">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-primary" />
                <h3 className="text-h3 text-landing-primary">Enterprise-level planning & reporting tools</h3>
              </div>
              <p className="text-body-lg text-landing-secondary leading-relaxed">
                Comprehensive dashboards and analytics for complex event portfolios. Our enterprise-grade platform
                provides the visibility and control you need to manage multiple events, track spending, and measure
                success across your entire organization.
              </p>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-primary" />
                <h3 className="text-h3 text-landing-primary">Real-time budget management & approvals</h3>
              </div>
              <p className="text-body-lg text-landing-secondary leading-relaxed">
                Streamlined workflows with integrated approval processes and spend tracking. Set budgets, define
                approval hierarchies, and monitor spending in real-time to maintain control and ensure compliance with
                corporate policies.
              </p>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-primary" />
                <h3 className="text-h3 text-landing-primary">Custom travel coordination for distributed teams</h3>
              </div>
              <p className="text-body-lg text-landing-secondary leading-relaxed">
                Specialized logistics for global teams with complex travel requirements. Coordinate flights,
                accommodations, and ground transportation for hundreds of attendees across multiple time zones and
                international locations.
              </p>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-primary" />
                <h3 className="text-h3 text-landing-primary">Flexible support - from fully managed to self-service</h3>
              </div>
              <p className="text-body-lg text-landing-secondary leading-relaxed">
                Choose your level of support based on your team's capacity and preferences. From white-glove
                full-service planning to self-service tools with expert consultation, we adapt our support model to
                match your organization's needs and resources.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Solutions Section - Document Style */}
      <section className="py-12 sm:py-24">
        <div className="max-w-3xl mx-auto">
          <div className="mb-16">
            <h2 className="text-h2 text-landing-primary mb-6">Solutions tailored to enterprise priorities</h2>
          </div>

          <div className="space-y-12">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-primary" />
                <h3 className="text-h3 text-landing-primary">Executive & board retreats</h3>
              </div>
              <p className="text-body-lg text-landing-secondary leading-relaxed">
                High-impact planning in inspiring settings that drive clarity and connection. Design strategic sessions
                that facilitate breakthrough thinking and strengthen leadership alignment. From intimate boardroom
                discussions to transformative off-site experiences that shape company direction.
              </p>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-primary" />
                <h3 className="text-h3 text-landing-primary">Global incentive trips</h3>
              </div>
              <p className="text-body-lg text-landing-secondary leading-relaxed">
                Design motivational programs that recognize top performers and reinforce culture. Create memorable
                reward experiences that inspire continued excellence while strengthening team bonds. From luxury
                destinations to unique adventures that celebrate achievement and drive future success.
              </p>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-primary" />
                <h3 className="text-h3 text-landing-primary">Internal summits & kickoffs</h3>
              </div>
              <p className="text-body-lg text-landing-secondary leading-relaxed">
                Align global teams, communicate priorities, and energize employees. Plan comprehensive gatherings that
                unify distributed teams around shared goals and company vision. Transform strategic initiatives into
                engaging experiences that motivate and inform your workforce.
              </p>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-primary" />
                <h3 className="text-h3 text-landing-primary">Client & partner events</h3>
              </div>
              <p className="text-body-lg text-landing-secondary leading-relaxed">
                Create unforgettable experiences that build loyalty and spark growth. Design sophisticated events that
                strengthen relationships, showcase innovation, and open new business opportunities. Make lasting
                impressions that translate into long-term partnerships and revenue growth.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Trusted by Leading Teams */}
      <Testimonial variant="single" person="yeon" title="Trusted by leading teams" />

      {/* FAQ Section */}
      <FAQComponent component={faqComponent} eventId={0} primaryColor="#5261EB" />

      {/* CTA Section */}
      <section className="py-12 sm:py-0">
        <div className="text-center">
          <CTA type="enterprise" />
        </div>
      </section>

      {/* Demo Modal */}
      <UnifiedModal
        type="cal"
        isOpen={isModalOpen("demo")}
        onClose={() => closeModal("demo")}
        title="Request a demo"
        subtitle="Schedule a 30-minute demo with our team"
        size="lg"
        calLink={DEMO_CALENDAR_LINK}
        fallbackText="Book your demo with our team"
      />
    </main>
  );
}
